import { css } from '@emotion/react';
import { t } from 'i18n';
import { useState } from 'react';
import { FEE_ITEM_TYPE, formatCurrency, isDefined } from 'shared';

import { DeviceType, useMaxDeviceWidth } from '../../hooks';
import { Divider } from '../Divider';
import { theme } from '../theme';
import { Tippy } from '../tippy/Tippy';

const styles = {
  label: (isClickable: boolean) => css`
    color: ${isClickable ? theme.colors.link : theme.colors.text};
    cursor: ${isClickable ? 'pointer' : 'default'};
  `,
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.sm};
  `,
  item: css`
    display: flex;
    justify-content: space-between;
  `,
  itemValue: css`
    padding-left: ${theme.spacing.xxxl};
  `,
  totalContainer: css`
    display: flex;
    justify-content: space-between;
    font-weight: ${theme.fontWeight.bold};
    font-size: ${theme.fontSize.md};
  `,
};

type FeeItem = {
  label: string;
  value: number;
};

export type PriceListItem = {
  details?: FeeItem[];
  itemType?: FEE_ITEM_TYPE;
} & FeeItem;

type Props = {
  priceListItem: PriceListItem;
  pointOfSale: string;
  text?: string;
  className?: string;
};

export const PriceDetail = ({ priceListItem, text, className, pointOfSale }: Props) => {
  const isSmall = useMaxDeviceWidth(DeviceType.Tablet);
  const [displayTooltip, setDisplayTooltip] = useState(false);

  const { details, label, value } = priceListItem;

  const isClickable = isDefined(details);

  const priceBreakdown = details?.map((priceItem, index) => {
    return (
      <div key={index} css={styles.item}>
        <div>{priceItem.label}</div>
        <div css={styles.itemValue}>{formatCurrency(priceItem.value, pointOfSale)}</div>
      </div>
    );
  });

  const content = (
    <div css={styles.container}>
      <h4>{label}</h4>

      <Divider />
      {priceBreakdown}
      <Divider />

      <div css={styles.totalContainer}>
        <div>{t('common:total')}</div>
        <div>{formatCurrency(value, pointOfSale)}</div>
      </div>
    </div>
  );

  return (
    <div>
      {(details && details.length > 0) || value !== 0 ? (
        <Tippy
          placement={'top-start'}
          offset={isSmall ? undefined : [-8, 16]}
          content={content}
          visible={displayTooltip}
          handleClose={() => {
            setDisplayTooltip(false);
          }}
        >
          <div
            css={styles.label(isClickable)}
            className={className}
            onClick={() => {
              isClickable ? setDisplayTooltip(prev => !prev) : null;
            }}
          >
            {text ?? label}
          </div>
        </Tippy>
      ) : null}
    </div>
  );
};
